import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import {
  AppBar,
  Box,
  CssBaseline,
  IconButton,
  ThemeProvider,
  Toolbar,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";

import theme from "./theme";
import ChatPage from "./components/pages/ChatPage";
import { useAppDispatch } from "core/store/reduxStore";
import { setBreadCrumbs, setHeader } from "core/store/shared/header";
import ConversationSidebar, {
  PentestAutomationConversation,
} from "./components/sidebar/ConversationSidebar";
import { chatService } from "modules/cstIasp/services/chatService";

const MODEL_ID =
  (process.env.REACT_APP_PENTESTAUTOMATION_MODEL as string) || "api-pentest";

function normalizeModelId(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase();
}

function PentestAutomation() {
  const dispatch = useAppDispatch();

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState<boolean>(false);
  const [conversations, setConversations] = useState<PentestAutomationConversation[]>([]);
  const [conversationId, setConversationId] = useState<string>("");

  useEffect(() => {
    localStorage.removeItem("pentestAutomation_conversationid");
    setConversationId("");
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  const refreshConversations = useCallback(async () => {
    setIsLoadingConversations(true);
    try {
      const list = await chatService.listConversationsViaApiConversations();
      const all = Array.isArray(list) ? (list as PentestAutomationConversation[]) : [];
      const activeModel = normalizeModelId(MODEL_ID);
      const filtered = activeModel
        ? all.filter((c) => normalizeModelId((c as any)?.model) === activeModel)
        : all;
      setConversations(filtered);
    } catch {
      setConversations([]);
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  const handleDeleteConversationId = useCallback(
    async (id: string) => {
      const conversationIdToDelete = (id || "").trim();
      if (!conversationIdToDelete) return;

      try {
        await chatService.deleteConversationViaApiConversations({
          conversationId: conversationIdToDelete,
        });
      } catch (e) {
        console.error("Failed to delete conversation", e);
      } finally {
        if (conversationIdToDelete === (conversationId || "").trim()) {
          setConversationId("");
          localStorage.removeItem("pentestAutomation_conversationid");
        }
        refreshConversations();
      }
    },
    [conversationId, refreshConversations]
  );

  useEffect(() => {
    dispatch(setHeader("API Pentest Automation"));
    dispatch(
      setBreadCrumbs([
        { displayText: "CYBER_STRATEGY", path: "/console" },
        {
          displayText: "Cyber Risk & Compliance",
          path: "/console/pentestAutomation/chat",
        },
        {
          displayText: "API Pentest Automation",
          path: "/console/pentestAutomation/chat",
        },
      ])
    );
  }, [dispatch]);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  const sidebarWidth = useMemo(() => 280, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          bgcolor: "background.default",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <AppBar
          position="static"
          color="primary"
          sx={(muiTheme) => ({
            background: `linear-gradient(135deg, ${muiTheme.palette.primary.light} 0%, ${muiTheme.palette.secondary.main} 100%)`,
          })}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label={sidebarCollapsed ? "open conversations" : "close conversations"}
              onClick={toggleSidebar}
              sx={{ mr: 2 }}
            >
              {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
              API Pentest Automation
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.8, fontStyle: "italic", mr: 1 }}>
                SecAI
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          sx={(muiTheme) => ({
            display: "flex",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            borderTop: `1px solid ${muiTheme.palette.divider}`,
          })}
        >
          <Box
            sx={(muiTheme) => ({
              width: sidebarCollapsed ? 0 : sidebarWidth,
              minWidth: sidebarCollapsed ? 0 : sidebarWidth,
              overflow: "hidden",
              transition: "width 0.3s ease, min-width 0.3s ease",
              borderRight: sidebarCollapsed ? "none" : `1px solid ${muiTheme.palette.divider}`,
              bgcolor: "background.paper",
            })}
          >
            <ConversationSidebar
              conversations={conversations}
              selectedConversationId={conversationId}
              onSelectConversationId={(id) => {
                setConversationId(id);
                localStorage.setItem("pentestAutomation_conversationid", id);
              }}
              onDeleteConversationId={handleDeleteConversationId}
              isLoading={isLoadingConversations}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "hidden", p: 2 }}>
            <Routes>
              <Route index element={<Navigate to="chat" replace />} />
              <Route
                path="chat"
                element={
                  <ChatPage
                    conversationId={conversationId}
                    onConversationIdChange={(id) => {
                      setConversationId(id);
                      localStorage.setItem("pentestAutomation_conversationid", id);
                      refreshConversations();
                    }}
                  />
                }
              />
              <Route
                path="*"
                element={
                  <Box sx={{ p: 2, color: "text.secondary" }}>
                    <Typography variant="body2">Not found</Typography>
                  </Box>
                }
              />
            </Routes>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default PentestAutomation;
